import { filterBy } from "@tql/ui-components-react/kendo/data-query";
import { useMemo } from "react";

import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useGridState } from "~/shared/hooks/use-grid-state.ts";
import type { RoutingGuideType } from "~/shared/schemas/routing-guide-schema.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { useRoutingGuideLayout } from "../../hooks/use-routing-guide-layout.ts";
import { rulesToString } from "../modal/utils/rules-to-string.ts";
import { getRoutingGuideColumns } from "./columns/routing-guide-columns.tsx";
import { useRoutingGuideGridState } from "./hooks/use-routing-guide-grid-state.ts";
import * as styles from "./index.module.css";

export const RoutingGuideGrid = ({
	onHandleOpenEditModal,
	dataState,
	onHandleDelete,
	isLoading,
	isCustomerLayout = false,
	canEdit,
}: {
	onHandleOpenEditModal: (guide: RoutingGuideType) => void;
	dataState?: RoutingGuideType[];
	onHandleDelete: (id: number) => void;
	isLoading: boolean;
	isCustomerLayout?: boolean;
	canEdit?: boolean;
}) => {
	const { isInternal } = useDeploymentType();

	// Persist the items-per-page selection for this grid via the shared
	// preference flow (per-user, restored on revisit / refresh / new session).
	const { skip, take, handlePageChange, isLoadingPreference } = useGridState(
		GridIdentifiers["Routing Guide"],
	);

	const { activeCustomer } = useRoutingGuideLayout();

	const columns = getRoutingGuideColumns(
		onHandleOpenEditModal,
		onHandleDelete,
		canEdit ?? isInternal,
		isCustomerLayout,
		activeCustomer?.id,
	);
	const { filter, handleFilterChange, sort, handleSortChange } =
		useRoutingGuideGridState();

	const processedData = useMemo(() => {
		const data = (dataState ?? []).map((item) => ({
			...item,
			details: rulesToString(item.rules),
			level: rulesToString(item.carrier),

			isGlobalSort: item.customerId == null ? 0 : 1,
		}));

		if (filter) {
			return filterBy(data, filter);
		}
		return data;
	}, [dataState, filter]);

	// Always prepend a global-first sort descriptor so global guides remain
	// above customer guides regardless of the user's chosen sort column.
	const globalFirstSort = useMemo(
		() => [{ field: "isGlobalSort", dir: "asc" as const }, ...sort],
		[sort],
	);

	return (
		<TraxGrid
			className={styles.routingGuideGrid}
			columns={columns}
			data={processedData}
			filter={filter}
			filterable={true}
			loading={isLoading || isLoadingPreference}
			loadingRows={take}
			onFilterChange={handleFilterChange}
			onPageChange={handlePageChange}
			onSortChange={handleSortChange}
			// Disabled until tql-ui-components upgrade
			resizable={false}
			skip={skip}
			sort={globalFirstSort}
			style={{ height: "100%" }}
			take={take}
		/>
	);
};
